<template>
  <div class="custom-filter-builder">
    <!-- Active Filter Chips (Stripe-style summary) -->
    <ActiveFilterChips
      :filters="allFilters"
      @remove="removeFilterFromChip"
      @clear-all="clearAll"
      @click-chip="scrollToFilter"
    />

    <!-- Filter Groups (OR logic between groups) -->
    <template v-for="(group, gIndex) in localConfig.groups">
      <!-- OR Connector (between groups) -->
      <div v-if="gIndex > 0" :key="'or-' + group.id" class="or-connector">
        <div class="or-line"></div>
        <span class="or-pill">OR</span>
        <div class="or-line"></div>
      </div>

      <FilterGroup
        :key="'group-' + group.id"
        :value="group"
        :proc-code-options="procCodeOptions"
        :zip-code-options="zipCodeOptions"
        :insurance-options="insuranceOptions"
        :provider-options="providerOptions"
        :recall-type-options="recallTypeOptions"
        :removable="localConfig.groups.length > 1"
        @input="updateGroup(gIndex, $event)"
        @remove="removeGroup(gIndex)"
      />
    </template>

    <!-- Bottom Actions -->
    <div class="builder-actions">
      <b-button variant="outline-secondary" size="sm" @click="addGroup">
        <i class="fa fa-plus mr-1"></i>Add OR Group
      </b-button>
      <b-button variant="primary" size="sm" @click="$emit('preview', localConfig)" :disabled="previewLoading || !isValid">
        <i class="fa fa-spinner fa-spin mr-1" v-if="previewLoading"></i>
        <i class="fa fa-search mr-1" v-else></i>
        {{ previewLoading ? 'Loading...' : 'Preview Results' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import ActiveFilterChips from './ActiveFilterChips.vue';
import FilterGroup from './FilterGroup.vue';

export default {
  name: 'CustomFilterBuilder',
  components: { ActiveFilterChips, FilterGroup },
  props: {
    value: {
      type: Object,
      default: () => ({
        type: 'customFilters',
        groups: [{ id: 1, filters: [] }]
      })
    },
    procCodeOptions: {
      type: Array,
      default: () => []
    },
    zipCodeOptions: {
      type: Array,
      default: () => []
    },
    insuranceOptions: {
      type: Object,
      default: () => ({ carriers: [], employers: [], groupNumbers: [] })
    },
    providerOptions: {
      type: Array,
      default: () => []
    },
    recallTypeOptions: {
      type: Array,
      default: () => []
    },
    previewLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localConfig: JSON.parse(JSON.stringify(this.value)),
      nextGroupId: 2
    };
  },
  computed: {
    allFilters() {
      const filters = [];
      for (const group of this.localConfig.groups) {
        for (const filter of group.filters) {
          filters.push({ ...filter, _groupId: group.id });
        }
      }
      return filters;
    },
    isValid() {
      if (this.localConfig.groups.length === 0) return false;
      const hasFilters = this.localConfig.groups.some(g => g.filters.length > 0);
      if (!hasFilters) return false;

      // Validate each filter's required fields
      for (const group of this.localConfig.groups) {
        for (const filter of group.filters) {
          if (filter.filterType === 'procedure') {
            if (filter.selectionMode === 'specific' && (!filter.procCodes || filter.procCodes.length === 0)) return false;
            if (!filter.statuses || filter.statuses.length === 0) return false;
            // Timeframe is optional (null = "any time / ever")
            if (filter.timeframe) {
              if (filter.timeframe.mode === 'custom') {
                if (!filter.timeframe.from || !filter.timeframe.to) return false;
                if (filter.timeframe.from > filter.timeframe.to) return false;
              } else {
                if (!filter.timeframe.value || !filter.timeframe.unit) return false;
              }
            }
          } else if (filter.filterType === 'appointment') {
            if (!filter.statuses || filter.statuses.length === 0) return false;
            if (!filter.timeframe || !filter.timeframe.value || !filter.timeframe.unit || !filter.timeframe.direction) return false;
          } else if (filter.filterType === 'insurance') {
            const hasCarriers = filter.carriers && filter.carriers.length > 0;
            const hasEmployers = filter.employers && filter.employers.length > 0;
            const hasGroupNumbers = filter.groupNumbers && filter.groupNumbers.length > 0;
            if (!hasCarriers && !hasEmployers && !hasGroupNumbers) return false;
          } else if (filter.filterType === 'demographic') {
            const hasAge = filter.ageMin !== null && filter.ageMin !== '' && filter.ageMin !== undefined;
            const hasGender = filter.gender && filter.gender !== 'all';
            const hasZips = filter.zipCodes && filter.zipCodes.length > 0;
            if (!hasAge && !hasGender && !hasZips) return false;
            if (hasAge) {
              const min = parseInt(filter.ageMin, 10);
              if (isNaN(min) || min < 0) return false;
              if (filter.ageMax !== null && filter.ageMax !== '' && filter.ageMax !== undefined) {
                const max = parseInt(filter.ageMax, 10);
                if (isNaN(max) || max < min) return false;
              }
            }
          } else if (filter.filterType === 'recall') {
            if (!filter.timeframe) return false;
            if (filter.timeframe.mode === 'custom') {
              if (!filter.timeframe.from || !filter.timeframe.to) return false;
              if (filter.timeframe.from > filter.timeframe.to) return false;
            } else {
              if (!filter.timeframe.value || !filter.timeframe.unit) return false;
            }
          }
        }
      }
      return true;
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.localConfig)) {
          this.localConfig = JSON.parse(JSON.stringify(newVal));
        }
      },
      deep: true
    },
    localConfig: {
      handler() {
        if (JSON.stringify(this.localConfig) !== JSON.stringify(this.value)) {
          this.$emit('input', JSON.parse(JSON.stringify(this.localConfig)));
        }
        this.$emit('valid', this.isValid);
      },
      deep: true
    },
    isValid: {
      immediate: true,
      handler(val) {
        this.$emit('valid', val);
      }
    }
  },
  created() {
    // Ensure at least one empty group exists
    if (!this.localConfig.groups || this.localConfig.groups.length === 0) {
      this.localConfig.groups = [{ id: 1, filters: [] }];
    }
    // Set nextGroupId beyond any existing IDs
    const maxId = Math.max(0, ...this.localConfig.groups.map(g => g.id));
    this.nextGroupId = maxId + 1;
  },
  methods: {
    updateGroup(index, updatedGroup) {
      const groups = [...this.localConfig.groups];
      groups[index] = updatedGroup;
      this.localConfig = { ...this.localConfig, groups };
    },
    removeGroup(index) {
      const groups = this.localConfig.groups.filter((_, i) => i !== index);
      this.localConfig = { ...this.localConfig, groups };
    },
    addGroup() {
      const id = this.nextGroupId++;
      const groups = [...this.localConfig.groups, { id, filters: [] }];
      this.localConfig = { ...this.localConfig, groups };
    },
    removeFilterFromChip(filter) {
      // Find the group containing this filter and remove it
      const groups = this.localConfig.groups.map(group => {
        if (group.id === filter._groupId) {
          return {
            ...group,
            filters: group.filters.filter(f => f.id !== filter.id)
          };
        }
        return group;
      });
      // Remove empty groups if more than one group remains
      const nonEmpty = groups.filter(g => g.filters.length > 0);
      this.localConfig = {
        ...this.localConfig,
        groups: nonEmpty.length > 0 ? nonEmpty : [groups[0] || { id: 1, filters: [] }]
      };
    },
    clearAll() {
      this.localConfig = {
        type: 'customFilters',
        groups: [{ id: 1, filters: [] }]
      };
    },
    scrollToFilter(filterId) {
      // Could implement scroll-to behavior here if needed
    }
  }
};
</script>

<style scoped lang="scss">
.custom-filter-builder {
  margin-top: 0.5rem;
}

.or-connector {
  display: flex;
  align-items: center;
  margin: 1rem 0;
  gap: 0.75rem;
}

.or-line {
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.or-pill {
  display: inline-block;
  padding: 0.25rem 1rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.builder-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.875rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}
</style>
